/**
 * Normalizes the JSON returned by Topol on save to store it in campaign `metadata`.
 *
 * @param {unknown} topolJson — JSON string or object depending on plugin version
 * @returns {object|undefined} — fragment ready to merge into `metadata`, or undefined if no data
 */
export function topolSaveJsonToMetadataFragment(topolJson) {
    if (topolJson == null || topolJson === '') {
        return undefined;
    }
    let config = topolJson;
    if (typeof topolJson === 'string') {
        try {
            config = JSON.parse(topolJson);
        } catch {
            config = topolJson;
        }
    }
    return { topolEditorConfig: config };
}

/**
 * Merges the Topol fragment into the campaign's existing `metadata` object.
 *
 * @param {object|null|undefined} existingMetadata
 * @param {unknown} topolJson
 * @returns {object|null}
 */
export function mergeTopolJsonIntoCampaignMetadata(existingMetadata, topolJson) {
    const fragment = topolSaveJsonToMetadataFragment(topolJson);
    if (!fragment) {
        return existingMetadata != null ? existingMetadata : null;
    }
    const base =
        existingMetadata && typeof existingMetadata === 'object' && !Array.isArray(existingMetadata)
            ? { ...existingMetadata }
            : {};
    return { ...base, ...fragment };
}

export function stableStringifyMetadata(metadata) {
    if (metadata == null) {
        return '';
    }
    try {
        return JSON.stringify(metadata);
    } catch {
        return String(metadata);
    }
}

/**
 * Indicates whether the campaign has Topol JSON stored in `metadata` (key `topolEditorConfig`).
 *
 * @param {object|null|undefined} metadata
 * @returns {boolean}
 */
export function hasTopolEditorMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
        return false;
    }
    const cfg = metadata.topolEditorConfig;
    return cfg != null && cfg !== '';
}

/**
 * Extracts the Topol JSON from the campaign metadata.
 * Returns the JSON string or null if it does not exist.
 *
 * @param {object|null|undefined} metadata
 * @returns {string|null}
 */
export function extractTopolJsonFromMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
        return null;
    }
    const cfg = metadata.topolEditorConfig;
    if (cfg == null || cfg === '') {
        return null;
    }
    // If already a string, return it directly
    if (typeof cfg === 'string') {
        return cfg;
    }
    // If it's an object, convert to JSON string
    try {
        return JSON.stringify(cfg);
    } catch {
        return null;
    }
}
